import { Link } from "react-router-dom";
import { Rocket, Users, Mountain, ShieldCheck, HeartHandshake, Check, X, ArrowRight, Link2, Wrench, Heart } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";
import { VALUES } from "@/data/content";

const VALUE_ICONS = { Rocket, Users, Mountain, ShieldCheck, HeartHandshake };

const PageHero = () => (
  <section className="bg-[#0B132B] text-white pt-40 pb-24 md:pb-32 grain relative overflow-hidden" data-testid="about-hero">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative z-10">
      <Reveal>
        <Overline light>About IBY</Overline>
        <h1 className="font-display font-semibold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.08] max-w-4xl">
          Built by people who know exactly what it feels like to not see yourself in the future being described.
        </h1>
      </Reveal>
    </div>
  </section>
);

const Story = () => (
  <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32" data-testid="about-story">
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
      <div className="lg:col-span-5">
        <Reveal>
          <img
            src="https://images.pexels.com/photos/9304654/pexels-photo-9304654.jpeg?auto=compress&cs=tinysrgb&w=900"
            alt="Mentor and student sharing ideas together"
            className="rounded-[2rem] w-full aspect-[3/4] object-cover"
          />
        </Reveal>
      </div>
      <div className="lg:col-span-7 flex flex-col justify-center">
        <Reveal>
          <Overline>Our Story</Overline>
          <h2 className="font-display font-semibold text-3xl md:text-5xl tracking-tight text-[#0B132B] mb-8">
            A world where opportunities are equal for every young person.
          </h2>
          <div className="space-y-6 text-[#374151] leading-[1.85] text-base md:text-lg max-w-2xl">
            <p>
              IBY — Inspired By You — began with a simple, uncomfortable observation: the students
              with the most drive were often the ones with the fewest doors. Culturally and
              Linguistically Diverse students, and young people facing disadvantage, were working
              twice as hard to access half as much — not for lack of talent, but for lack of
              networks, role models and tailored guidance.
            </p>
            <p>
              Our purpose is to inspire culturally diverse students to believe in their potential, help them see
              their opportunities, and equip them for a fulfilling life. We do it the only way that
              genuinely works: expert, tailored, in-person mentoring that bridges identity,
              confidence and opportunity.
            </p>
            <p>
              We are not an inspiration machine. Motivation fades by Monday morning; access lasts a
              lifetime. IBY ensures students don't just dream about careers — they see them, access
              them, and step confidently into them.
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const WhyItMatters = () => (
  <section className="bg-white py-24 md:py-32" data-testid="about-why-it-matters">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14">
      <div className="lg:col-span-4">
        <Reveal>
          <Overline>Why It Matters</Overline>
          <h2 className="font-display font-semibold text-3xl md:text-4xl tracking-tight text-[#0B132B]">
            Generic mentoring wasn't built for these students.
          </h2>
        </Reveal>
      </div>
      <div className="lg:col-span-8">
        <Reveal delay={0.15}>
          <div className="space-y-6 text-[#374151] leading-[1.85] text-base md:text-lg">
            <p>
              Most career programs assume a starting line that many culturally diverse students never had: parents
              who navigated this system, family friends in professional roles, an intuitive sense of
              how workplaces operate. When those assumptions fail, the advice fails with them —
              however well-intentioned it may be.
            </p>
            <p>
              IBY starts from a different place. We treat culture and lived experience not as
              barriers to work around, but as strengths to build upon. Our mentors are matched for
              relatability as much as expertise. Our programs are face-to-face, because trust is
              built in person. And our outcomes are concrete — subject plans, work placements, job
              offers — because hope needs somewhere to land.
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const PILLARS = [
  { Icon: Link2, title: "Creating Connections", text: "Relatable mentors, real workplaces and warm professional networks — the connections most students inherit, made available to all." },
  { Icon: Wrench, title: "Developing Skills", text: "From self-awareness to interview mastery — practical, career-ready skills built step by step across the school-to-work journey." },
  { Icon: Heart, title: "Human-Centred Programs", text: "Face-to-face, tailored, identity-first. Every program is designed around the whole young person — never a generic curriculum." },
];

const Pillars = () => (
  <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32" data-testid="about-pillars">
    <Reveal>
      <Overline>How IBY Delivers</Overline>
      <h2 className="font-display font-semibold text-3xl md:text-5xl tracking-tight text-[#0B132B] max-w-2xl mb-16">
        Three pillars hold up every program we run.
      </h2>
    </Reveal>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {PILLARS.map((p, i) => (
        <Reveal key={p.title} delay={i * 0.12}>
          <div className="group bg-white rounded-[2rem] p-10 h-full border border-black/5 hover:border-[#FF5A36]/40 hover:-translate-y-2 transition-[transform,border-color] duration-500">
            <div className="w-14 h-14 rounded-2xl bg-[#FF5A36]/10 flex items-center justify-center mb-8 group-hover:bg-[#FF5A36] transition-colors duration-500">
              <p.Icon size={26} className="text-[#FF5A36] group-hover:text-white transition-colors duration-500" />
            </div>
            <h3 className="font-display font-semibold text-2xl tracking-tight text-[#0B132B] mb-4">{p.title}</h3>
            <p className="text-[#4A5568] leading-relaxed">{p.text}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const Values = () => (
  <section className="bg-[#0B132B] text-white py-24 md:py-32 grain relative overflow-hidden" data-testid="about-values">
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative z-10">
      <Reveal>
        <Overline light>Our Values</Overline>
        <h2 className="font-display font-semibold text-3xl md:text-5xl tracking-tight max-w-2xl mb-16">
          Five words we hold ourselves to.
        </h2>
      </Reveal>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
        {VALUES.map((v, i) => {
          const Icon = VALUE_ICONS[v.icon];
          return (
            <Reveal key={v.name} delay={i * 0.08}>
              <div className="bg-white/5 border border-white/10 rounded-[1.5rem] p-8 h-full hover:bg-[#FF5A36] transition-colors duration-500 group">
                <Icon size={28} className="text-[#FFB323] group-hover:text-white transition-colors duration-500 mb-6" />
                <h3 className="font-display font-semibold text-xl mb-3">{v.name}</h3>
                <p className="text-white/60 group-hover:text-white/90 text-sm leading-relaxed transition-colors duration-500">{v.desc}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

const DIFFERENT = [
  { us: true, text: "Expert mentors matched for culture, identity and aspiration" },
  { us: true, text: "Face-to-face, tailored delivery — never a webinar link" },
  { us: true, text: "A continuous journey from Year 9 to first job" },
  { us: true, text: "Real workplace access through corporate partners" },
  { us: false, text: "One-off motivational talks with no follow-through" },
  { us: false, text: "Generic advice that ignores identity and lived experience" },
];

const Different = () => (
  <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32" data-testid="about-different">
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
      <Reveal>
        <Overline>What Makes IBY Different</Overline>
        <h2 className="font-display font-semibold text-3xl md:text-5xl tracking-tight text-[#0B132B] mb-6">
          Inspiration is the start. Access is the point.
        </h2>
        <p className="text-[#4A5568] leading-relaxed max-w-lg mb-8">
          Plenty of programs make students feel good for an afternoon. IBY is built to change what
          actually happens next — subjects chosen, doors opened, careers begun.
        </p>
        <Link
          to="/services"
          data-testid="about-explore-programs-btn"
          className="inline-flex items-center gap-2 rounded-full bg-[#FF5A36] text-white px-8 py-4 font-bold hover:bg-[#E04825] transition-colors duration-300"
        >
          Explore Our Programs <ArrowRight size={18} />
        </Link>
      </Reveal>
      <Reveal delay={0.15}>
        <div className="bg-white rounded-[2rem] border border-black/5 p-8 md:p-10 space-y-5">
          {DIFFERENT.map((d) => (
            <div key={d.text} className="flex items-start gap-4">
              <span className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${d.us ? "bg-[#FF5A36]/10 text-[#FF5A36]" : "bg-black/5 text-[#9CA3AF]"}`}>
                {d.us ? <Check size={15} strokeWidth={3} /> : <X size={15} strokeWidth={3} />}
              </span>
              <p className={`leading-relaxed ${d.us ? "text-[#0B132B] font-semibold" : "text-[#9CA3AF] line-through decoration-1"}`}>{d.text}</p>
            </div>
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);

export default function About() {
  return (
    <main data-testid="about-page">
      <PageHero />
      <Story />
      <WhyItMatters />
      <Pillars />
      <Values />
      <Different />
    </main>
  );
}
